import { UICorePlugin } from "../../../../lib/Plugin";
import { circleParse, circleStringify } from './circleJson';
import { forIn, forOwn, get, omit, omitBy, set, unset } from "lodash-es";
import { arrayify } from "../../../../lib/utils/array";
import { openJSONEditModal } from "../../../../lib/builtin/plugins/MockProxy/JSONEditModal";
/**
 * mock方案
 * ucDataSources{
 *  data:{
 *    instance:{
 *      data1:
 *      data2:
 *    }
 *  schema:{
 *    fields:[]
 *    }
 *  }
 * }
 * 对ucDataSources的操作有以下几种
 * 1. addDataSource instance赋值 需要代理data.set
 * 2. addSourceDeclaration直接对this.ucDataSources[id]赋值 赋值后data/state基础对象会变 失去Proxy
 *    检测到之后重新包装proxy
 * mock数据注入后可以知道 [数据源]:instance[key] 被mock了
 * 所以使用Map<string, Set<string>> 保存 [数据源]:[key1,key2,key3] 在集合内的都标记为mock数据
 * 这样就可以检测ucDataSources操作时 仅拦截对mock数据的修改, 添加新数据源可以正常使用
 * */
/** 插件包含4个钩子
 * 1.pageletRendererCreatedMockStart CRenderer中代理ucDataSources
 * 2.pageletRendererCollectRuntimeData CRenderer中收集运行时数据
 * 3.setMockState 外部ui修改mock状态
 * 4.getActionsOfDataSource 添加局部修改按钮(M)到数据源面板
 * */
export class MockProxyPlugin extends UICorePlugin {
  constructor() {
    super();
    this.isMockOn = false;
    this.dataSourceProxyHandler = this.getDataSourceProxyHandler();
    this.ucDataSourcesProxyHandler = this.getUcDataSourcesProxyHandler();
  }
  install(uc) {
    this.isMockOn = false;
    this.mockedKeysInDataSource = new Map();
    this.ucDataSourcesProxyObject = {};
    this.mockDataRaw = {
      ucDataSources: {}
    };
    uc.isDesignMode && uc.hooks.pageletRendererCreatedMockStart.tap('MockProxyPlugin', renderer => this.pageletRendererCreatedMockStart(renderer));
    uc.isDesignMode && uc.hooks.setMockState.tap('MockProxyPlugin', (isMockOnArg, mockCase, designer) => this.setMockState(isMockOnArg, mockCase, designer));
    uc.hooks.pageletRendererCollectRuntimeData.tap('MockProxyPlugin', (mockData, renderer) => this.pageletRendererCollectRuntimeData(mockData, renderer));
    uc.isDesignMode && uc.hooks.getActionsOfDataSource.tap('MockProxyPlugin', (list, ctx) => this.getActionsOfDataSource(list, ctx));
  }
  getActionsOfDataSource(list, ctx) {
    var _this$mockedKeysInDat;
    const h = this.$createElement;
    if (!this.isMockOn) return list;
    const {
      dataPath
    } = ctx;
    // 按照dataPath判断是否mock
    const [pathSource, pathInstance] = dataPath.split('.');
    if (!pathSource) return;
    // 要么是数据源 要么是数据源中的某一个key
    const isMockedPath = !pathInstance && this.mockedKeysInDataSource.get(pathSource) || pathInstance && ((_this$mockedKeysInDat = this.mockedKeysInDataSource.get(pathSource)) === null || _this$mockedKeysInDat === void 0 ? void 0 : _this$mockedKeysInDat.has(pathInstance));
    if (!isMockedPath) return;
    const ans = [...list];
    ans.unshift({
      key: 'mockEdit',
      icon() {
        const h = this.$createElement;
        return h("div", ["M"]);
      },
      title: 'mock数据编辑',
      execute: async () => {
        var _res$data;
        const {
          dataPath,
          dataStub
        } = ctx;
        const mockPath = this.transDataPath(dataPath);
        const mockState = ctx.designer.mockState;
        const fullMockData = mockState.getMock(mockState.mockCaseUsed);
        const mockValue = get(fullMockData.ucDataSources, mockPath);
        const {
          schema
        } = dataStub;
        const schemaPath = this.transSchemaPath(dataPath, fullMockData);
        const options = {
          schemaPath,
          schema: JSON.stringify(schema),
          path: dataPath,
          source: JSON.stringify(circleStringify(mockValue), null, 2)
        };
        const res = await openJSONEditModal(options);
        if (!res.success) return;
        // 删掉某一个数据源的mock
        if (res.message === 'delete' && schemaPath !== undefined) {
          unset(fullMockData.ucDataSources, mockPath);
          unset(fullMockData.ucDataSources, schemaPath);
          await mockState.save(JSON.stringify(circleStringify(fullMockData)));
          mockState.useMock(fullMockData.uuid);
          return;
        }
        if (res.message === 'change' && (_res$data = res.data) !== null && _res$data !== void 0 && _res$data.source) {
          var _res$data2;
          const changedData = JSON.parse((_res$data2 = res.data) === null || _res$data2 === void 0 ? void 0 : _res$data2.source);
          // 按照dataPath写回到Mock里 然后重新打一遍mock
          set(fullMockData.ucDataSources, mockPath, changedData);
          // 保存数据
          await mockState.save(JSON.stringify(circleStringify(fullMockData)));
          // 用新的数据启动mock
          mockState.useMock(fullMockData.uuid);
          return;
        }
      }
    });
    return ans;
  }
  /** data.test1 转换为data.instance.test1 */
  transDataPath(path) {
    if (path.split('.').length > 1) {
      // 做一次到instance的转换
      const dataPathSplit = path.split('.');
      dataPathSplit.splice(1, 0, 'instance');
      return dataPathSplit.join('.');
    }
    return path;
  }
  /** 按照dataPath查找对应的schemaPath(在Mock数据ucDatasource中的路径) */
  transSchemaPath(dataPath, fullMockData) {
    const splitArray = dataPath.split('.');
    // 关闭打开 需要以单个数据源/变量 为单位
    if (splitArray.length > 2) {
      return undefined;
    }
    // 是一个数据源中的key 补充上schema的路径 并查找fields里对应的index
    if (splitArray.length === 2) {
      var _get;
      // 要找index 然后插入schema
      const [dataSourcePath, dataId] = splitArray;
      const schemaIndex = (_get = get(fullMockData, ['ucDataSources', dataSourcePath, 'schema', 'fields'])) === null || _get === void 0 ? void 0 : _get.findIndex(field => field.id === dataId);
      return [dataSourcePath, 'schema', 'fields', schemaIndex].join('.');
    }
    return dataPath;
  }
  pageletRendererCreatedMockStart(renderer) {
    if (!renderer.ucInternalIsRoot || !this.isMockOn) return;
    this.ucDataSourcesProxyObject = renderer.ucDataSources;
    renderer.ucDataSources = new Proxy(this.ucDataSourcesProxyObject, this.ucDataSourcesProxyHandler);
    // 操作ob同步value和新的实例
    // @ts-ignore
    renderer.ucDataSources.__ob__.value = renderer.ucDataSources;
    // TODO:wContext的实例是inject进来的需要包裹proxy
    // delete this.mockDataRaw.ucDataSources.wContext;
    Object.values(this.mockDataRaw.ucDataSources).map(value => ({
      ...value,
      isMock: 'MOCKED'
    }))
    // 在fields里打上'mocked' state-tag
    .map(value => {
      var _value$schema;
      const newFields = value === null || value === void 0 || (_value$schema = value.schema) === null || _value$schema === void 0 || (_value$schema = _value$schema.fields) === null || _value$schema === void 0 ? void 0 : _value$schema.map(field => ({
        ...field,
        'uc:tag': arrayify(field['uc:tag']).concat('mocked')
      }));
      if (newFields) {
        // 浅拷贝schema
        return {
          ...value,
          schema: {
            ...(value === null || value === void 0 ? void 0 : value.schema),
            fields: newFields
          }
        };
      }
      return value;
    })
    // 对每个dataSource都包裹Proxy
    .map(value => new Proxy(value, this.dataSourceProxyHandler)).forEach(value => {
      if ((value === null || value === void 0 ? void 0 : value.instance) instanceof Object) {
        const instanceKeySet = new Set(Object.keys(value.instance));
        this.mockedKeysInDataSource.set(value.id, instanceKeySet);
        console.log('[MockProxyPlugin]:collectKeys', value, instanceKeySet);
      } else {
        console.log('[MockProxyPlugin] 数据源缺少instance', value);
      }
      renderer.addDataSource(value);
    });
    // debugger;
    console.log('[MockProxyPlugin]:mock数据注入完成');
  }
  setMockState(isMockOnArg, mockCase, designer) {
    this.isMockOn = isMockOnArg;
    if (!this.isMockOn) {
      // 关闭Mock
      setTimeout(() => designer.recreateCanvas());
      return;
    }
    // 深拷贝
    // 将mock数据注入插件中
    const clonedMockCase = JSON.parse(JSON.stringify(mockCase));
    this.mockDataRaw = circleParse(clonedMockCase);
    // 重置canvas 启动mock流程
    setTimeout(() => designer.recreateCanvas());
    console.log('[MockProxyPlugin]:启动mock');
  }
  /** 从CRenderer里收集运行时数据 */
  pageletRendererCollectRuntimeData(mockData, renderer) {
    var _wContext$query, _wContext$config, _wContext$query2, _wContext$config2, _renderer$ctx;
    // @ts-ignore wContext是inject进来的 不知道为什么不在type里面
    const {
      ucDataSources,
      wContext
    } = renderer;
    if (!ucDataSources) return null;
    // 要按照wContext的schema切一下
    const schemaCut = (instance, schema) => {
      if (!(instance instanceof Object)) return instance;
      const newObj = Array.isArray(instance) ? [] : {};
      // 有原型链上下来的属性
      forIn(instance, (value, key) => {
        var _schema$fields;
        if (!(schema !== null && schema !== void 0 && schema.fields)) {
          return newObj[key] = value;
        }
        const subSchema = (_schema$fields = schema.fields) === null || _schema$fields === void 0 ? void 0 : _schema$fields.find(obj => obj.id === key);
        if (subSchema) {
          newObj[key] = schemaCut(value, subSchema);
        }
      });
      return newObj;
    };
    const cutWcontext = () => {
      var _ucDataSources$wConte;
      const schemaWcontext = ucDataSources === null || ucDataSources === void 0 || (_ucDataSources$wConte = ucDataSources.wContext) === null || _ucDataSources$wConte === void 0 ? void 0 : _ucDataSources$wConte.schema;
      const wcontextInstance = omit(wContext, 'lessCode');
      return schemaCut(wcontextInstance, schemaWcontext);
    };
    // 把ucDataSources上其他属性和instance整理一下
    const mergeInstanceAndSchema = () => {
      let ucDataSourcesMerge = {};
      forOwn(ucDataSources, (value, key) => {
        const newSources = {
          ...value,
          instance: renderer[key] || value
        };
        newSources.instance = schemaCut(newSources.instance, newSources.schema);
        ucDataSourcesMerge[key] = newSources;
      });
      ucDataSourcesMerge = omitBy(ucDataSourcesMerge, (_, key) => key[0] === '$');
      return ucDataSourcesMerge;
    };
    let outputObject = {
      projectId: (wContext === null || wContext === void 0 || (_wContext$query = wContext.query) === null || _wContext$query === void 0 ? void 0 : _wContext$query.projectid) || (wContext === null || wContext === void 0 ? void 0 : wContext.projectId) || (wContext === null || wContext === void 0 || (_wContext$config = wContext.config) === null || _wContext$config === void 0 ? void 0 : _wContext$config.projectId),
      pageId: (wContext === null || wContext === void 0 || (_wContext$query2 = wContext.query) === null || _wContext$query2 === void 0 ? void 0 : _wContext$query2.pageid) || (wContext === null || wContext === void 0 || (_wContext$config2 = wContext.config) === null || _wContext$config2 === void 0 ? void 0 : _wContext$config2.pageId),
      mockCaseName: '',
      // @ts-ignore ctx也是inject到renderer里的
      creatorName: renderer === null || renderer === void 0 || (_renderer$ctx = renderer.ctx) === null || _renderer$ctx === void 0 || (_renderer$ctx = _renderer$ctx.user) === null || _renderer$ctx === void 0 ? void 0 : _renderer$ctx.name,
      createTime: Date.now(),
      ucDataSources: {
        ...mergeInstanceAndSchema(),
        wContext: {
          ...ucDataSources.wContext,
          instance: cutWcontext()
        }
      }
    };
    outputObject = circleStringify(outputObject, 0, []);
    // 调整Key的顺序
    forOwn(outputObject.ucDataSources, (value, key) => {
      const newValue = {
        id: value.id,
        instance: value.instance
      };
      outputObject.ucDataSources[key] = {
        ...newValue,
        ...value
      };
    });
    // 核心mock数据
    return outputObject;
  }
  /** 当向ucDataSources挂载新的数据源时会被这里捕捉到 */
  getUcDataSourcesProxyHandler() {
    return {
      set: (target, p, value, receiver) => {
        const newDataSource = this.patchMockDataSources(value, Reflect.get(target, p, receiver));
        // patch进mock数据
        const res = Reflect.set(target, p, newDataSource);
        console.log('[MockProxyPlugin]:ucDataSources Proxy set', p, this.mockedKeysInDataSource.has(newDataSource.id));
        return res;
      },
      defineProperty: (target, p, attributes) => {
        // 只有最开始挂上__ob__.value的才会调用这个 剩余的都可以被set监听到
        const success = Reflect.defineProperty(target, p, attributes);
        console.log('[MockProxyPlugin]:ucDataSources defineProperty ', p, attributes, Reflect.get(target, p));
        return success;
      }
    };
  }
  getDataSourceProxyHandler() {
    return {
      set: (target, p, value, receiver) => {
        // 设置 instance
        if (p === 'instance') {
          // 新的instance被挂载了 但是dataSchema是不变的 或者dataSchema已经被处理完了
          const oldInstance = Reflect.get(target, p, receiver);
          // 移除setter
          const newInstance = value;
          if (oldInstance instanceof Object) {
            Object.entries(oldInstance).forEach(([key, value]) => {
              var _this$mockedKeysInDat2;
              // TODO:如果 value不是对象 怎么区分呢?  应该用key做区分 而不是 用value做区分
              // 用key也更好打标记
              // 旧的非objectValue会直接覆盖,始终保持锁定mock值
              if ((_this$mockedKeysInDat2 = this.mockedKeysInDataSource.get(target.id)) !== null && _this$mockedKeysInDat2 !== void 0 && _this$mockedKeysInDat2.has(key)) {
                if (key in newInstance) {
                  // TODO:产生了数据覆盖 通知用户?
                }
                // TODO:考虑保留原有属性 使用defineProperty去做隐藏属性
                // newInstance[`_origin_${key}`] = newInstance[key];
                // 挂载新的value到实例上 考虑到vue __ob 必须使用defineProperty
                Object.defineProperty(newInstance, key, {
                  value,
                  configurable: true,
                  enumerable: true,
                  writable: true
                });
                console.log('[MockProxyPlugin]:dataSourceProxyHandler COVER instance', key, value);
              }
            });
          }
          console.log('[MockProxyPlugin]:dataSourceHandler set instance', target, value);
          return Reflect.set(target, p, newInstance);
        }
        return Reflect.set(target, p, value);
      }
    };
  }
  patchMockDataSources(newDataSource, oldDataSource) {
    // 如果不是mock数据就不管他了
    if (!this.mockedKeysInDataSource.has(oldDataSource.id)) {
      return newDataSource;
    }
    const patchedKey = [];
    oldDataSource.instance instanceof Object && Object.entries(oldDataSource.instance).forEach(([key, value]) => {
      var _this$mockedKeysInDat3;
      if ((_this$mockedKeysInDat3 = this.mockedKeysInDataSource.get(oldDataSource.id)) !== null && _this$mockedKeysInDat3 !== void 0 && _this$mockedKeysInDat3.has(key)) {
        patchedKey.push(key);
        newDataSource.instance[key] = value;
      }
    });
    // 按照patchedKey 从old的dataSchema中取出对应的 比较新的schema是不是不一样
    const getSchemaIndexById = (dataSource, id) => {
      var _dataSource$schema;
      return (_dataSource$schema = dataSource.schema) === null || _dataSource$schema === void 0 || (_dataSource$schema = _dataSource$schema.fields) === null || _dataSource$schema === void 0 ? void 0 : _dataSource$schema.findIndex(val => (val === null || val === void 0 ? void 0 : val.id) === id);
    };
    const getSchemaById = (dataSource, id) => {
      var _dataSource$schema2;
      return (_dataSource$schema2 = dataSource.schema) === null || _dataSource$schema2 === void 0 || (_dataSource$schema2 = _dataSource$schema2.fields) === null || _dataSource$schema2 === void 0 ? void 0 : _dataSource$schema2.find(val => (val === null || val === void 0 ? void 0 : val.id) === id);
    };
    patchedKey.forEach(key => {
      const newSchemaIndex = getSchemaIndexById(newDataSource, key);
      const oldSchema = getSchemaById(oldDataSource, key);
      const newSchema = getSchemaById(newDataSource, key);
      // 比较一下 如果不一样就提示用户
      // 如果已有dataSchema 替换
      if (oldSchema && newSchema) {
        newDataSource.schema.fields[newSchemaIndex] = oldSchema;
      } else {
        var _newDataSource$schema;
        // 如果 是新增的(已删除)数据源 就新增schema
        (_newDataSource$schema = newDataSource.schema) === null || _newDataSource$schema === void 0 || (_newDataSource$schema = _newDataSource$schema.fields) === null || _newDataSource$schema === void 0 || _newDataSource$schema.push(oldSchema);
      }
    });
    return new Proxy(newDataSource, this.dataSourceProxyHandler);
  }
}
//# sourceMappingURL=MockProxy.js.map
